import React from "react";
import { 
  Container, Grid, Typography, Button, CircularProgress, Box, 
  Card, CardContent, Divider, Avatar
} from '@material-ui/core';
import { 
  NavigateNext, Sync, SignalCellularAlt, SignalCellularOff, 
  GpsFixed, GpsOff, CheckCircle, Schedule, Person
} from '@material-ui/icons';
import { withRouter } from "react-router-dom";
import ApiService from './services/ApiService';
import { Service } from './services/DBService';
import { Helper } from './services/Helpers';

class Home extends React.Component {
  
  constructor(props) {
    super(props)
    this.state = {
      gpsAvailable: false,
      latitude: 0,
      longitude: 0,
      syncItemCount: 0,
      inspectionsInProgress: 0,
      lastSync: localStorage.getItem("last_sync"),
      isLoading: false,
      isOnline: false
    }
    
    this._sync = this._syncToApi.bind(this);
    this._forceSync = this._forceSync.bind(this);
    this._getUserData = this._getUserData.bind(this);
    this._getSyncItemCount = this._getSyncItemCount.bind(this);
    this._goToInspections = this._goToInspections.bind(this);
    this._geoLocationSuccess = this._geoLocationSuccess.bind(this);
    this._checkOnlineStatus = this._checkOnlineStatus.bind(this);

  }

  _forceSync() {
    this._sync();
  }

  async _processBatches(batches, retryQueue, maxRetries) {
    for (const batch of batches) {
      try {
        const results = await ApiService.postData(batch);
  
        for (const record of batch) {
          const result = results.find((r) => r.uuid === record.uuid);
  
          if (result && result.status === 'success') {
            // Remove successfully synced item from IndexedDB
            const changeKey = await Service.getKeyFromIndex('Changelog', 'uniqueId', result.uuid);
            await Service.delete('Changelog', changeKey);
          } else {
            // Add failed item to retry queue
            this._addToRetryQueue(record, retryQueue, maxRetries);
          }
        }
      } catch (err) {
        console.error('Batch failed:', err);
        // Add entire batch to retry queue
        batch.forEach((record) => this._addToRetryQueue(record, retryQueue, maxRetries));
      }
    }
  }

  async _processRetryQueue(retryQueue, maxRetries) {
    const failedAgain = [];
  
    for (const record of retryQueue) {
      try {
        const results = await ApiService.postData([record]); // Single-item batch
        const result = results && results[0];
  
        if (result && result.status === 'success') {
          // Successfully synced on retry; remove from IndexedDB
          const changeKey = await Service.getKeyFromIndex('Changelog', 'uniqueId', result.uuid);
          await Service.delete('Changelog', changeKey);
        } else {
          // Add to failedAgain if retry still fails
          if (record.retries < maxRetries) {
            record.retries += 1;
            failedAgain.push(record);
          } else {
            console.error(`Max retries reached for uuid: ${record.uuid}`);
          }
        }
      } catch (err) {
        console.error(`Retry failed for uuid: ${record.uuid}`, err);
        if (record.retries < maxRetries) {
          record.retries += 1;
          failedAgain.push(record);
        }
      }
    }
  
    retryQueue.length = 0; // Clear old queue
    retryQueue.push(...failedAgain); // Update with remaining failures
  } 
  
  _addToRetryQueue(record, retryQueue, maxRetries) {
    const retryItem = retryQueue.find((item) => item.uuid === record.uuid);
  
    if (retryItem) {
      if (retryItem.retries < maxRetries) {
        retryItem.retries += 1;
      } else {
        console.error(`Max retries reached for uuid: ${record.uuid}`);
      }
    } else {
      retryQueue.push({ ...record, retries: 1 });
    }
  }  

  async _syncToApi(force = false) {
    this.setState({ isLoading: true });
  
    // Get all completed inspections using the statusIndex
    // Using lowerBound with empty string to match any non-null string value (UUIDs)
    const completedInspections = await Service.getAllFromIndex('Inspections', 'statusIndex', IDBKeyRange.lowerBound(""));
    const completedInspectionIds = new Set(completedInspections.map(inspection => inspection.id));
  
    // Get changelog items for completed inspections using the inspectionIndex
    const inspectionChangeDataPromises = Array.from(completedInspectionIds).map(inspectionId => 
      Service.getAllFromIndex('Changelog', 'inspectionIndex', inspectionId.toString())
    );

    const inspectionChangeDataArrays = await Promise.all(inspectionChangeDataPromises);
    const inspectionChangeData = inspectionChangeDataArrays.flat();
    
    // Get all Work Order changelog items (no status check needed)
    const allChangelogItems = await Service.getAll('Changelog');
    const workOrderChangeData = allChangelogItems.filter(change => 
      change.work_order_id !== undefined && change.work_order_id !== null
    );
    
    // Combine inspection and work order changelog items
    const changeData = [...inspectionChangeData, ...workOrderChangeData];
  
    const imageRelatedTypes = ['addimage', 'woaddimage', 'newdefect'];
    const batchSize = 10; // Ideal batch size for non-image records
    const retryQueue = []; // Temporary retry queue for this sync session
    const maxRetries = 3; // Maximum retries for each record
  
    Helper.checkOnlineStatus()
      .then(async (status) => {
        if (localStorage.getItem('authenticated') === 'true' && status === true) {
          if (changeData.length > 0) {
            // Categorize data into image and non-image batches
            const imageBatches = [];
            const nonImageBatches = [];
  
            changeData.forEach((record) => {
              if (imageRelatedTypes.includes(record.change_type)) {
                imageBatches.push([record]); // Image-heavy records in separate batches
              } else {
                const lastBatch = nonImageBatches[nonImageBatches.length - 1];
                if (!lastBatch || lastBatch.length >= batchSize) {
                  nonImageBatches.push([record]); // Start a new batch
                } else {
                  lastBatch.push(record); // Add to the current batch
                }
              }
            });
  
            // Process all batches
            await this._processBatches(imageBatches, retryQueue, maxRetries);
            await this._processBatches(nonImageBatches, retryQueue, maxRetries);
  
            // Retry failed items
            if (retryQueue.length > 0) {
              await this._processRetryQueue(retryQueue, maxRetries);

              if (retryQueue.length > 0) {
                alert('Sync Error => Some items failed to sync. Try again later.');
                this.setState({ isLoading: false }); // Stop loading if there is an error
              }
            }
  
            // Finalize sync
            this._getApiData(); // Clear and resync to server
          } else {
            this._getApiData();
          }
        } else {
          console.error('Sync Error => Authorisation Refused');
          this.props.history.push('/Login');
        }
      })
      .catch((error) => {
        alert('Sync Error => Connection Error');
        console.error(error);
        this.setState({ isLoading: false }); // Stop loading if there is an error
      })
  }  

  _getUserData() {
    Service.getAll('User').then((user) => { 

      if ( user.length > 0 )
        this.setState({ username: user[0].first_name + ' ' + user[0].last_name });
      else {
        localStorage.removeItem('last_sync');
        localStorage.setItem('authenticated', 'false');
        this.setState({ lastSync: null });
      }
    });
  }

  _getSyncItemCount() {    
    // Count total changelog items pending sync
    Service.getAll('Changelog').then((changes) => { 
      this.setState({ syncItemCount: changes.length });
      
      // Count unique inspections in progress (with changelog items)
      const uniqueInspections = new Set();
      changes.forEach(change => {
        if (change.inspection) {
          uniqueInspections.add(change.inspection);
        }
      });
      this.setState({ inspectionsInProgress: uniqueInspections.size });
    });

    Service.getAll('Inspections').then((inspections) => { 
      const completedInspections = inspections.filter(inspection => inspection.inspection_status !== null);
      const certifiedInspectionsCount = completedInspections.length;
      
      const isOnline = Helper.checkOnlineStatus();

      // Auto-sync if there are completed inspections and we're online
      isOnline.then((status) => {
        if (( certifiedInspectionsCount > 0 ) && ( status === true )) {
          this._sync();
        }
      });
    });
  }

  _getApiData() {

    if(localStorage.getItem("authenticated") === 'true') {
      
      ApiService.fetchData()
      .then(
        (data) => {
          if (!data) {
            console.error('fetchData returned no data');
            alert('Sync Error => Could not retrieve data from server');
            this.setState({ isLoading: false });
            return;
          }

          if (data.status === 'badaccesskey') {
            console.log('LOGOUT: badaccesskey from server');
            localStorage.setItem('authenticated', 'false');
            this.props.history.push('/Login');
          } else {
            Service.eraseDatabase(['Changelog'])
            .then((response) => {
              this._initDB(data);
              this._getSyncItemCount();
              this._getUserData();

              localStorage.setItem('last_sync', new Date());
              alert('Data synchronisation complete.')
              this.setState({ lastSync: new Date(), isLoading: false });
            })
            .catch((err) => {
              console.error('Error during database erase:', err);
              alert('Sync Error => Database error occurred');
              this.setState({ isLoading: false });
            });
          }
        }
      ).catch(
        (err) => {
          console.error('fetchData failed:', err);
          alert('Sync Error => Connection Error');
          this.setState({ isLoading: false });
        }
      );
    }
  }

  async _initDB(apidata) {
    //console.log(apidata);

    let { user, areas, asset_classes, inspection_set, priorities, statuses, work_order_set } = apidata;

    Service.put('User', {
      id: user.id,
      first_name: user.first_name,
      last_name: user.last_name,
      email: user.email
    });

    for (let i = 0; i < areas.length; i++) {
      let area = areas[i];
      Service.put('Tracks', {
        id: area.id,
        name: area.name,
        site: area.site,
        kms_from: area.kms_from,
        kms_to: area.kms_to
      });
    }

    for (let i = 0; i < asset_classes.length; i++) {
      let asset = asset_classes[i];
      Service.put('Assets', {
        id: asset.id,
        name: asset.name,
        site: asset.site != null ? asset.site : 0,
        kms_from: asset.kms,
        kms_to: asset.kms_to,
        latitude: asset.latitude,
        longitude: asset.longitude
      });
    }

    for (let i = 0; i < inspection_set.length; i++) {
      let inspection = inspection_set[i];
      let inspectionId = inspection.inspection.id.toString();
      
      // Get all changelog items for this inspection
      const changelogItems = await Service.getAllFromIndex('Changelog', 'inspectionIndex', inspectionId);
      
      // Initialize arrays if they don't exist
      if (!inspection.inspection_data) inspection.inspection_data = [];
      if (!inspection.inspection.notes) inspection.inspection.notes = [];
      
      // Initialize tracks structure if needed
      if (!inspection.inspection.tracks) inspection.inspection.tracks = {};
      
      // Process inspection notes from changelog
      for (const change of changelogItems) {
        if (change.change_type === 'newinspectionnote') {
          // Check if this note already exists
          const noteExists = inspection.inspection.notes.some(note => 
            note.uuid === change.uuid
          );
          
          if (!noteExists) {
            inspection.inspection.notes.push({
              uuid: change.uuid,
              created_by: this.state.username,
              inspection_note: {
                created_on: change.action_datetime,
                title: change.title,
                inspection_note: change.inspection_note
              }
            });
          }
        }
      }
      
      // Process new defects from changelog
      const newDefects = changelogItems.filter(change => 
        change.change_type === 'newdefect'
      );
      
      for (const change of newDefects) {
        // Check if this defect already exists in the inspection data
        const defectExists = inspection.inspection_data.some(d => 
          d.defect.id === change.defect || d.defect.id === change.app_tmp_id
        );
        
        if (!defectExists) {
          // Create a new defect entry matching the structure
          const newDefectEntry = {
            defect: {
              id: change.app_tmp_id,
              app_tmp_id: change.app_tmp_id,
              short_id: 'PENDING', // Will show as pending until synced
              asset_class: change.asset_class,
              defect_status: change.defect_status,
              priority: change.priority,
              area: change.area,
              kms_start: change.kms_start,
              kms_finish: change.kms_finish,
              description: change.description,
              latitude: change.latitude,
              longitude: change.longitude,
              repair_due_date: change.repair_due_date,
              repair_completed: null,
              date_reported: change.action_datetime.split(' ')[0], // Extract date
              maintenance_required: change.maintenance_required
            },
            defect_notes: [],
            defect_images: [],
            area: change.area
          };
          
          // Add any images from the newdefect imgdata
          if (change.imgdata && Array.isArray(change.imgdata)) {
            for (const imgData of change.imgdata) {
              newDefectEntry.defect_images.push({
                title: imgData.imgtitle || '',
                description: imgData.imgdesc || '',
                created_on: change.action_datetime,
                image_file: imgData.value
              });
            }
          }
          
          inspection.inspection_data.push(newDefectEntry);
        }
      }
      
      // Process each defect and merge changelog data
      if (inspection.inspection_data && Array.isArray(inspection.inspection_data)) {
        for (let defectData of inspection.inspection_data) {
          const defectId = defectData.defect.id.toString();
          const trackId = defectData.area;
          
          // Initialize track if it doesn't exist
          if (!inspection.inspection.tracks[trackId].inspected) {
            inspection.inspection.tracks[trackId] = {
              id: trackId,
              name: inspection.inspection.tracks[trackId].name,
              defect_order_reverse: false,
              inspected: [],
              defect_order: []
            };
          }
          
          // Get changelog items for this specific defect
          const defectChanges = changelogItems.filter(change => 
            change.defect && (
              change.defect.toString() === defectId || 
              change.app_tmp_id === defectId
            )
          );
          
          // Merge defect notes from changelog
          for (const change of defectChanges) {
            if (change.change_type === 'newdefectnote') {
              // Check if this note already exists (by uuid or content)
              const noteExists = defectData.defect_notes?.some(note => 
                note.uuid === change.uuid
              );
              
              if (!noteExists) {
                if (!defectData.defect_notes) defectData.defect_notes = [];
                defectData.defect_notes.unshift({
                  uuid: change.uuid,
                  created_by: this.state.username,
                  defect_note: {
                    created_on: change.action_datetime,
                    defect_note_title: change.defect_note_title,
                    defect_note: change.defect_note
                  }
                });
              }
              
              // Set inspection status flags and track inspected defects
              if (change.defect_note_title === 'Inspected') {
                defectData.inspection_status = change.uuid;
                
                // Add to inspected array if not already there
                if (!inspection.inspection.tracks[trackId].inspected.includes(defectId)) {
                  inspection.inspection.tracks[trackId].inspected.push(defectId);
                }
              } else if (change.defect_note_title === 'Defect Reassessed') {
                defectData.reassessed_status = change.uuid;
                
                // Add to inspected array if not already there
                if (!inspection.inspection.tracks[trackId].inspected.includes(defectId)) {
                  inspection.inspection.tracks[trackId].inspected.push(defectId);
                }
              }
            }
            
            // Merge images from changelog
            if (change.change_type === 'addimage') {
              const imageExists = defectData.defect_images?.some(img => 
                img.created_on === change.action_datetime
              );
              
              if (!imageExists) {
                if (!defectData.defect_images) defectData.defect_images = [];
                defectData.defect_images.unshift({
                  title: change.imgtitle || '',
                  description: change.imgdesc || '',
                  created_on: change.action_datetime,
                  image_file: change.value
                });
              }
            }
            
            // Merge property updates from changelog
            if (change.change_type === 'defect_update') {
              // Set defect_changed status
              if (( change.item !== 'latitude' ) && ( change.item !== 'longitude' ))
                defectData.defect_changed = true;

              // Update the defect property based on the 'item' field
              if (change.item && defectData.defect[change.item] !== undefined) {
                defectData.defect[change.item] = change.value;
              }
            }
          }
        }
      }
      
      // Store the inspection with merged data
      Service.put('Inspections', {
        id: inspection.inspection.id,
        name: inspection.inspection.name,
        client_id: inspection.inspection.client_id,
        client_name: inspection.inspection.client_name,
        client_short_name: inspection.inspection.client_short_name,
        event_date: inspection.inspection.event_date,
        planned_date: inspection.inspection.planned_date,
        iteration: inspection.inspection.iteration_days,
        tolerance: inspection.inspection.tolerance_days,
        site: inspection.inspection.site,
        site_name: inspection.inspection.site_name,
        inspection_status: null,
        skip_p4: inspection.inspection.skip_p4,
        worksite_safety_complete: inspection.inspection.worksite_safety_complete,
        notes: inspection.inspection.notes,
        tracks: inspection.inspection.tracks,
        defects: inspection.inspection_data
      });
    }

    for (let i = 0; i < priorities.length; i++) {
      let priority = priorities[i];
      Service.put('Priorities', {
        id: priority.id,
        name: priority.name,
        client: priority.client,
        colour: priority.colour
      });
    }

    for (let i = 0; i < statuses.length; i++) {
      let status = statuses[i];
      Service.put('Statuses', {
        id: status.id,
        name: status.name,
        colour: status.colour
      });
    }

    for (let i = 0; i < work_order_set.length; i++) {
      let workorder = work_order_set[i];
      Service.put('WorkOrders', {
        id: workorder.id,
        name: workorder.name,
        site: workorder.site,
        inspection: workorder.inspection,
        work_type: workorder.work_type,
        event_date: workorder.event_date,
        signoff_datedue: workorder.signoff_datedue,
        primary_activity: workorder.primary_activity,
        secondary_activity: workorder.secondary_activity,
        work_summary: workorder.work_summary,
        works_as_executed: workorder.works_as_executed,
        images: []
      });
    }
  }

  _goToInspections() {
    this.props.history.push('/inspections/');
  }

  _geoLocationSuccess(position) {
    //console.log("Latitude is :", position.coords.latitude);
    //console.log("Longitude is :", position.coords.longitude);

    const latitude = position.coords.latitude;
    const longitude = position.coords.longitude;

    this.setState({
      latitude: latitude,
      longitude: longitude
    });
  }

  _checkOnlineStatus() {
    const isOnline = Helper.checkOnlineStatus();
    isOnline.then((status) => {
      //console.log(status);
      this.setState({ isOnline: status, gpsAvailable: (("geolocation" in navigator) && status) });
    });
  }

  componentDidMount() {
    this.Timer = setInterval(() => {
      this._checkOnlineStatus();
    }, 1000);

    this._checkOnlineStatus();

    if ( navigator.geolocation ) {

      var options = {
        enableHighAccuracy: true,
        timeout: 6000,
        maximumAge: 30000
      };

      function error(err) {
        console.warn(`ERROR(${err.code}): ${err.message}`);
        alert('Unable to get your location. Please make sure you have location services enabled.');
      }

      if ( this.state.gpsAvailable )
        navigator.geolocation.getCurrentPosition(this._geoLocationSuccess, error, options);
    }
    
    // Check if this is first login or user changed (no last_sync)
    if ( !this.state.lastSync || this.state.lastSync === 'null' ) {
      this.setState({ isLoading: true });
      // Force full database clear on first login (including Changelog)
      Service.eraseDatabase()  // Full clear, no exclusions
        .then(() => {
          this._getApiData();
        })
        .catch(err => {
          console.error('Error clearing database on first login:', err);
          // Try to proceed anyway - _getApiData will do partial clear
          this._getApiData();
        });
    } else {
      this._getUserData();
      this._getSyncItemCount();
    }

    //console.log('sync => ' + this.state.lastSync);
  }

  componentWillUnmount() {
    clearInterval(this.Timer);
  }

  render() {
    //console.log('Home');
    //console.log(this.props.data);
    if(!this.state.isLoading) {
      return (
        <Container maxWidth="md">
          <Box py={3}>
            <Grid container spacing={3}>
              {/* Header Card */}
              <Grid item xs={12}>
                <Card elevation={2}>
                  <CardContent>
                    <Box display="flex" alignItems="center" mb={2}>
                      <Avatar style={{ backgroundColor: '#3f51b5', marginRight: 16 }}>
                        <Person />
                      </Avatar>
                      <Box>
                        <Typography variant="h5" component="h1">
                          Welcome Back
                        </Typography>
                        <Typography variant="body1" color="textSecondary">
                          {this.state.username || 'Loading...'}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* Status Card */}
              <Grid item xs={12}>
                <Card elevation={2}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      App Status
                    </Typography>
                    <Divider style={{ marginBottom: 16 }} />
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Box display="flex" alignItems="center">
                          {this.state.isOnline ? (
                            <SignalCellularAlt style={{ color: '#4caf50', marginRight: 8 }} />
                          ) : (
                            <SignalCellularOff style={{ color: '#f44336', marginRight: 8 }} />
                          )}
                          <Typography variant="body2">
                            {this.state.isOnline ? 'Online' : 'Offline'}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box display="flex" alignItems="center">
                          {this.state.gpsAvailable ? (
                            <GpsFixed style={{ color: '#4caf50', marginRight: 8 }} />
                          ) : (
                            <GpsOff style={{ color: '#9e9e9e', marginRight: 8 }} />
                          )}
                          <Typography variant="body2">
                            {this.state.gpsAvailable ? 'GPS Enabled' : 'GPS Unavailable'}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                    <Box mt={2}>
                      <Typography variant="caption" color="textSecondary">
                        App Version: {this.props.app_version}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* Work Progress Card */}
              <Grid item xs={12}>
                <Card elevation={2}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Work Progress
                    </Typography>
                    <Divider style={{ marginBottom: 16 }} />
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Box textAlign="center" py={2}>
                          <Box display="flex" alignItems="center" justifyContent="center" mb={1}>
                            <Schedule style={{ color: '#ff9800', marginRight: 0, fontSize: 32 }} />
                          </Box>
                          <Typography variant="h4" style={{ color: '#ff9800' }}>
                            {this.state.inspectionsInProgress}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            Inspection{this.state.inspectionsInProgress !== 1 ? 's' : ''} in Progress
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box textAlign="center" py={2}>
                          <Box display="flex" alignItems="center" justifyContent="center" mb={1}>
                            <CheckCircle 
                              style={{ 
                                color: this.state.syncItemCount > 0 ? '#2196f3' : '#4caf50', 
                                marginRight: 0, 
                                fontSize: 32 
                              }} 
                            />
                          </Box>
                          <Typography 
                            variant="h4" 
                            style={{ 
                              color: this.state.syncItemCount > 0 ? '#2196f3' : '#4caf50' 
                            }}
                          >
                            {this.state.syncItemCount}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            Item{this.state.syncItemCount !== 1 ? 's' : ''} Pending Upload
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* Action Buttons */}
              <Grid item xs={12}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Button 
                      variant="contained" 
                      color="primary" 
                      fullWidth 
                      size="large"
                      startIcon={<Sync />}
                      onClick={this._forceSync}
                      disabled={!this.state.isOnline}
                      style={{ padding: '16px', fontSize: '1.1rem' }}
                    >
                      Sync Data
                    </Button>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Button 
                      variant="contained" 
                      color="secondary" 
                      fullWidth 
                      size="large"
                      endIcon={<NavigateNext />}
                      onClick={this._goToInspections}
                      disabled={this.state.lastSync === null}
                      style={{ padding: '16px', fontSize: '1.1rem' }}
                    >
                      Inspections
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Box>
        </Container>
      );
    } else {
      return (
        <Container maxWidth="md">
          <Box py={3}>
            <Card elevation={2}>
              <CardContent>
                <Box textAlign="center" py={4}>
                  <CircularProgress size={60} />
                  <Typography variant="h6" style={{ marginTop: 24 }}>
                    Synchronizing Data...
                  </Typography>
                  <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
                    Please wait while we update your local data
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Container>
      );
    }
  }
}

export default withRouter(Home);